import { mergeRaceHistory } from './mergeRaceHistory'
import type { Race } from 'src/types/domain/Race'

/**
 * Short-lived, in-memory cache for the past-races deep read.
 *
 * The panel unmounts when it is closed, so without this a player toggling
 * "Past Games" re-issues the full window read every time. One entry per game
 * visited, so unbounded growth is not a concern in a single session.
 *
 * The window only needs to be roughly current: the live rounds are merged in
 * from the socket-fed store on every render, so a slightly stale cached tail
 * still yields a correct, up-to-date list.
 *
 * `now` is a parameter rather than a `Date.now()` call so the behaviour is
 * testable without fake timers.
 */
export const RACE_HISTORY_TTL_MS = 60_000
const TTL_MS = RACE_HISTORY_TTL_MS

/**
 * Ceiling on the accumulated window. A panel left open all day would otherwise
 * grow without bound — at crash's ~1,700 rounds/day this is roughly half a
 * day of play, so a player will not see it bite, but the tab cannot balloon.
 */
export const RACE_HISTORY_MAX = 1_000

type Entry = Readonly<{ at: number; races: Race[] }>

const cache = new Map<string, Entry>()

/**
 * Undefined means "go and refetch", NOT "there is nothing here". The stale
 * entry is deliberately LEFT IN PLACE so the write that follows the refetch
 * merges into it — deleting it would make every refresh start from an empty
 * window and clamp the count straight back to one page.
 */
export const readRaceHistoryCache = (gameId: string, now: number): Race[] | undefined => {
  const entry = cache.get(gameId)
  if (!entry) return undefined
  if (now - entry.at >= TTL_MS) return undefined

  return entry.races
}

/**
 * ACCUMULATES rather than replaces. Each refresh brings the newest page of
 * rounds; merging it into what we already hold means the window grows for as
 * long as the player keeps the game open, and "Last N rounds" climbs instead
 * of snapping back to ~200 every minute (which read as the panel resetting
 * itself). Only a page load — which drops this module — or switching game
 * starts the window over.
 *
 * The incoming copy wins on a round we already hold: it is the fresher read,
 * so it carries any events that landed since.
 */
export const writeRaceHistoryCache = (gameId: string, races: Race[], now: number): void => {
  // An empty array is what the hook produces when the read FAILS. Caching it
  // would pin the panel to the store's ~10 rounds for the whole TTL — and,
  // worse here, merging it would be a no-op that still reset the clock.
  if (races.length === 0) return

  const held = cache.get(gameId)?.races ?? []
  // mergeRaceHistory resolves collisions to its FIRST argument and returns
  // newest-first, so the fresh read goes first to win over what we hold.
  const merged = mergeRaceHistory(races, held).slice(0, RACE_HISTORY_MAX)

  cache.set(gameId, { at: now, races: merged })
}

/**
 * In-flight reads, keyed by game. Two surfaces can ask for the same window at
 * once (the sidebar panel and the stats popover), and toggling a panel quickly
 * re-mounts its hook — without this, each of those issues its own full-window
 * read, because the cache is only written when a read COMPLETES.
 */
const pending = new Map<string, Promise<Race[]>>()

/**
 * The single entry point: serve from cache, join an in-flight read, or start
 * one. Callers never touch read/write directly.
 */
export const loadRaceHistory = (
  gameId: string,
  now: number,
  fetchRaces: () => Promise<Race[]>
): Promise<Race[]> => {
  const cached = readRaceHistoryCache(gameId, now)
  if (cached) return Promise.resolve(cached)

  const inFlight = pending.get(gameId)
  if (inFlight) return inFlight

  const request = fetchRaces()
    .then((races) => {
      writeRaceHistoryCache(gameId, races, now)
      // Hand back the ACCUMULATED window, not just the page we just fetched —
      // the caller renders whatever this resolves to, and returning the page
      // alone would show one page however much we had gathered.
      return cache.get(gameId)?.races ?? races
    })
    .finally(() => {
      // Cleared on BOTH paths: a settled failure must not park later callers
      // behind it (an empty result is never cached, so the retry is free).
      pending.delete(gameId)
    })

  pending.set(gameId, request)
  return request
}

export const clearRaceHistoryCache = (): void => {
  cache.clear()
  pending.clear()
}
